import { useMutation } from '@tanstack/react-query';

import {
  useRequest,
  type MutationFunction,
  type MutationFunctionContext,
  type RequestQueryObject,
} from '@frontend/api';

import type { Signal } from './types';

export type SignalFromHintsRawCallout = {
  desc?: string | null;
  loc: [number, number, number, number];
  rawText: string;
  callout: Record<string, any>; // Using 'any' for JSON compatibility
};

export type SignalFromHintsRequestBody =
  | { rawText: string }
  | { rawCallout: SignalFromHintsRawCallout };

export type SignalFromHintsResponse = Signal;

export type SignalFromHintsError = {
  error: boolean;
  code: 'invalid';
  message: string;
  extra?: Record<string, unknown>;
};

const useGetSignalFromHintsKey = ['rules', 'signal-from-hints'] as const;
export const getUseGetSignalFromHintsKey = () => useGetSignalFromHintsKey;

/**
 * Mutation function for getting a signal from hints (raw text or raw callout).
 * Creates a Signal skeleton with appropriate document_path based on the provided hints.
 *
 * @param raw_text - Text content to create signal from
 * @param raw_callout - Callout object with desc, loc, rawText, and callout data
 */
export const useGetSignalFromHints: MutationFunction<
  SignalFromHintsResponse,
  SignalFromHintsRequestBody,
  [],
  RequestQueryObject,
  SignalFromHintsError
> = ({ requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useMutation<
    SignalFromHintsResponse,
    SignalFromHintsError,
    SignalFromHintsRequestBody,
    MutationFunctionContext<SignalFromHintsResponse>
  >(
    (body) =>
      request<SignalFromHintsResponse>('/rules/signal_from_hints/', {
        init: {
          method: 'POST',
          body,
        },
      }),
    {
      ...queryOptions,
      mutationKey: getUseGetSignalFromHintsKey(),
    },
  );
};
